import { useEffect, useState } from 'react';
import type { TraderProfile } from '@shared/types';

import { Panel, PanelHeader } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { InlineError } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useUpdateProfile } from '@/lib/mutations';
import { toMessage } from '@/lib/api';
import { absoluteDate } from '@/lib/format';

const PHONE_PATTERN = /^\+?[0-9 ()-]{10,20}$/;

export function ProfileSection({ profile }: { profile: TraderProfile }) {
  const toast = useToast();
  const update = useUpdateProfile();
  const [phone, setPhone] = useState(profile.phone ?? '');
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);

  useEffect(() => {
    setPhone(profile.phone ?? '');
  }, [profile.phone]);

  const isDirty = phone.trim() !== (profile.phone ?? '');

  const save = async (): Promise<void> => {
    const trimmed = phone.trim();
    if (trimmed && !PHONE_PATTERN.test(trimmed)) {
      setFieldError('Введите номер в международном формате.');
      return;
    }

    setFieldError(undefined);
    setError(null);
    try {
      await update.mutateAsync({ phone: trimmed || null });
      toast.success('Профиль обновлён');
    } catch (updateError) {
      setError(toMessage(updateError, 'Не удалось сохранить профиль.'));
    }
  };

  return (
    <Panel padding="none">
      <PanelHeader title="Профиль" description="Почту аккаунта здесь изменить нельзя." />

      <div className="space-y-4 px-4 py-4">
        {error ? <InlineError message={error} /> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="eyebrow">Почта</p>
            <p className="num mt-1 break-all text-base text-fg">{profile.email}</p>
          </div>
          <div>
            <p className="eyebrow">Регистрация</p>
            <p className="num mt-1 text-base text-fg">{absoluteDate(profile.createdAt)}</p>
          </div>
        </div>

        <div className="max-w-prose border-t border-line pt-4">
          <TextField
            label="Телефон"
            type="tel"
            inputMode="tel"
            enterKeyHint="done"
            monospace
            autoComplete="tel"
            placeholder="+7 900 000 00 00"
            hint="Нужен только для восстановления доступа. Оставьте поле пустым, чтобы удалить номер."
            value={phone}
            error={fieldError}
            disabled={update.isPending}
            onChange={(event) => {
              setPhone(event.target.value);
              setFieldError(undefined);
            }}
          />

          <Button
            variant="primary"
            className="mt-4"
            disabled={!isDirty}
            isLoading={update.isPending}
            onClick={() => void save()}
          >
            {update.isPending ? 'Сохраняю…' : 'Сохранить изменения'}
          </Button>
        </div>
      </div>
    </Panel>
  );
}
